import React from "react";
import PropTypes from "prop-types";
import styled, { css, keyframes } from "styled-components";
import { motion, useReducedMotion } from "framer-motion";

import Button from "./Button.jsx";
import { colors } from "../../../../theme";
import { pointerHover } from "../../../styles/pointer-hover";

/**
 * Aceternity-style stateful button (Family-inspired):
 * label stays visible; loader/check animate in beside it;
 * hover shows an offset ring.
 * @see https://ui.aceternity.com/components/stateful-button
 */

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Face = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Label = styled(motion.span)`
  display: inline-flex;
`;

const Glyph = styled.svg`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: inherit;
  display: block;

  ${(props) =>
    props.$spin &&
    css`
      animation: ${spin} 0.3s linear infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

const Root = styled(Button)`
  ${(props) =>
    props.$busy &&
    css`
      pointer-events: none;
      cursor: wait;
    `}

  /* Mirrors Aceternity: ring-offset-2 + hover:ring-2 */
  ${pointerHover(css`
    &:hover:not(:disabled):not([aria-busy="true"]) {
      box-shadow:
        0 0 0 2px ${colors.white},
        0 0 0 4px ${colors.black};
    }
  `)}

  &:focus-visible:not(:disabled):not([aria-busy="true"]) {
    box-shadow:
      0 0 0 2px ${colors.white},
      0 0 0 4px ${colors.black};
  }

  &:disabled {
    box-shadow: none;
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const iconHide = {
  width: 0,
  opacity: 0,
  marginRight: 0,
};

const iconShow = {
  width: 20,
  opacity: 1,
  marginRight: 8,
};

const STATUS_ANNOUNCE = {
  loading: "Submitting",
  success: "Success",
  error: "Error",
};

const iconSlotStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  flexShrink: 0,
};

const AnimatedIcon = ({ active, spin: shouldSpin, children }) => (
  <motion.span
    style={iconSlotStyle}
    initial={false}
    animate={active ? iconShow : iconHide}
    transition={{ duration: 0.2 }}
  >
    <Glyph
      $spin={shouldSpin && active}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </Glyph>
  </motion.span>
);

const StatefulButton = ({ children, state = "", disabled, ...props }) => {
  const reduceMotion = useReducedMotion();
  const activeState = STATUS_ANNOUNCE[state] ? state : "";
  const isLoading = activeState === "loading";
  const isSuccess = activeState === "success";
  const isError = activeState === "error";

  return (
    <Root
      $busy={isLoading}
      disabled={disabled}
      aria-busy={isLoading || undefined}
      {...props}
    >
      <Face layout={!reduceMotion}>
        <AnimatedIcon active={isLoading} spin>
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M12 3a9 9 0 1 0 9 9" />
        </AnimatedIcon>
        <AnimatedIcon active={isSuccess}>
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
          <path d="M9 12l2 2l4 -4" />
        </AnimatedIcon>
        <AnimatedIcon active={isError}>
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
          <path d="M10 10l4 4M14 10l-4 4" />
        </AnimatedIcon>
        <Label layout={!reduceMotion}>{children}</Label>
        {activeState ? (
          <VisuallyHidden aria-live="polite">
            {STATUS_ANNOUNCE[activeState]}
          </VisuallyHidden>
        ) : null}
      </Face>
    </Root>
  );
};

StatefulButton.propTypes = {
  children: PropTypes.node.isRequired,
  state: PropTypes.oneOf(["", "loading", "success", "error"]),
  disabled: PropTypes.bool,
};

export default StatefulButton;
